import { useState, DragEvent, TouchEvent, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import styles from "./TwoColumnListInput.module.css";
import TwoColumnListItem from "./TwoColumnListItem";

export interface TwoColumnBlock {
  id: string;
  label: string;
  icon?: string;
}

interface TwoColumnListInputProps {
  leftValue: string[];
  rightValue: string[];
  onLeftChange: (newOrder: string[]) => void;
  onRightChange: (newOrder: string[]) => void;
  availableBlocks: TwoColumnBlock[];
  leftLabel?: string;
  rightLabel?: string;
  disabled?: boolean;
  itemClassName?: string;
  itemIconClassName?: string;
  itemLabelClassName?: string;
}

type ColumnType = "left" | "right";

type DraggedItem = {
  id: string;
  sourceColumn: ColumnType;
  sourceIndex: number;
} | null;

export default function TwoColumnListInput({
  leftValue = [],
  rightValue = [],
  onLeftChange,
  onRightChange,
  availableBlocks,
  leftLabel = "Left Column",
  rightLabel = "Right Column",
  disabled = false,
  itemClassName,
  itemIconClassName,
  itemLabelClassName,
}: TwoColumnListInputProps) {
  const [draggedItem, setDraggedItem] = useState<DraggedItem>(null);
  const draggedItemRef = useRef<DraggedItem>(null);

  const [currentLeftOrder, setCurrentLeftOrder] = useState<string[]>(leftValue);
  const [currentRightOrder, setCurrentRightOrder] =
    useState<string[]>(rightValue);

  const currentLeftRef = useRef<string[]>(leftValue);
  const currentRightRef = useRef<string[]>(rightValue);

  const updateLeft = (order: string[]) => {
    currentLeftRef.current = order;
    setCurrentLeftOrder(order);
  };

  const updateRight = (order: string[]) => {
    currentRightRef.current = order;
    setCurrentRightOrder(order);
  };

  const setDragged = (item: DraggedItem) => {
    draggedItemRef.current = item;
    setDraggedItem(item);
  };

  useEffect(() => {
    if (!draggedItemRef.current) {
      updateLeft(leftValue);
      updateRight(rightValue);
    }
  }, [leftValue, rightValue]);

  const handleDragStart = (
    e: DragEvent<HTMLDivElement>,
    id: string,
    column: ColumnType,
    index: number,
  ) => {
    if (disabled) return;
    updateLeft(leftValue);
    updateRight(rightValue);
    setDragged({ id, sourceColumn: column, sourceIndex: index });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    if (draggedItemRef.current) {
      onLeftChange(currentLeftRef.current);
      onRightChange(currentRightRef.current);
    }
    setDragged(null);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (targetColumn: ColumnType, targetIndex: number) => {
    if (disabled || !draggedItemRef.current) return;

    const { id, sourceColumn, sourceIndex } = draggedItemRef.current;

    if (sourceColumn === targetColumn && sourceIndex === targetIndex) return;

    if (sourceColumn === targetColumn) {
      const currentOrder =
        targetColumn === "left"
          ? [...currentLeftRef.current]
          : [...currentRightRef.current];

      const actualSourceIndex = currentOrder.indexOf(id);
      if (actualSourceIndex === -1 || actualSourceIndex === targetIndex) return;

      currentOrder.splice(actualSourceIndex, 1);
      currentOrder.splice(targetIndex, 0, id);

      if (targetColumn === "left") {
        updateLeft(currentOrder);
      } else {
        updateRight(currentOrder);
      }

      setDragged({ id, sourceColumn: targetColumn, sourceIndex: targetIndex });
    } else {
      const sourceOrder =
        sourceColumn === "left"
          ? [...currentLeftRef.current]
          : [...currentRightRef.current];
      const targetOrder =
        targetColumn === "left"
          ? [...currentLeftRef.current]
          : [...currentRightRef.current];

      const actualSourceIndex = sourceOrder.indexOf(id);
      if (actualSourceIndex === -1) return;

      sourceOrder.splice(actualSourceIndex, 1);
      targetOrder.splice(targetIndex, 0, id);

      if (sourceColumn === "left") {
        updateLeft(sourceOrder);
        updateRight(targetOrder);
      } else {
        updateLeft(targetOrder);
        updateRight(sourceOrder);
      }

      setDragged({ id, sourceColumn: targetColumn, sourceIndex: targetIndex });
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  useEffect(() => {
    const onDragEnd = () => {
      if (draggedItemRef.current) {
        onLeftChange(currentLeftRef.current);
        onRightChange(currentRightRef.current);
        setDragged(null);
      }
    };

    document.addEventListener("dragend", onDragEnd);
    return () => document.removeEventListener("dragend", onDragEnd);
  }, []);

  const handleTouchStart = (
    e: TouchEvent<HTMLDivElement>,
    id: string,
    column: ColumnType,
    index: number,
  ) => {
    if (disabled) return;
    updateLeft(leftValue);
    updateRight(rightValue);
    setDragged({ id, sourceColumn: column, sourceIndex: index });
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (disabled || !draggedItemRef.current) return;
    e.preventDefault();

    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);

    if (element) {
      const itemElement = element.closest("[data-two-column-item]");
      if (itemElement) {
        const targetColumn = itemElement.getAttribute(
          "data-column",
        ) as ColumnType;
        const targetIndex = parseInt(
          itemElement.getAttribute("data-index") || "0",
        );

        if (targetColumn && !isNaN(targetIndex)) {
          handleDragEnter(targetColumn, targetIndex);
        }
      }
    }
  };

  const handleTouchEnd = () => {
    if (draggedItemRef.current) {
      onLeftChange(currentLeftRef.current);
      onRightChange(currentRightRef.current);
      setDragged(null);
    }
  };

  const getBlockData = (blockId: string) => {
    return availableBlocks.find((b) => b.id === blockId);
  };

  const displayLeftOrder = draggedItem ? currentLeftOrder : leftValue;
  const displayRightOrder = draggedItem ? currentRightOrder : rightValue;

  const renderColumn = (
    columnType: ColumnType,
    columnData: string[],
    columnLabel: string,
  ) => {
    const isDraggingFromThisColumn = draggedItem?.sourceColumn === columnType;

    return (
      <div
        className={styles.column}
        onDrop={(e) => {
          e.preventDefault();
          handleDragEnd();
        }}
        onDragOver={(e) => e.preventDefault()}
      >
        {" "}
        <Typography variant="h6" className={styles.columnLabel}>
          {columnLabel}
        </Typography>
        <div className={styles.listContainer}>
          {columnData.length === 0 ? (
            <div className={styles.emptyColumn}>
              <Typography className={styles.emptyText}>
                {isDraggingFromThisColumn && draggedItem
                  ? "Drop here"
                  : "No items"}
              </Typography>
            </div>
          ) : (
            <>
              {columnData.map((blockId, index) => {
                const blockData = getBlockData(blockId);
                if (!blockData) return null;

                const isDragging =
                  draggedItem?.id === blockId &&
                  draggedItem?.sourceColumn === columnType &&
                  draggedItem?.sourceIndex === index;

                return (
                  <TwoColumnListItem
                    key={`${columnType}-${blockId}`}
                    block={blockData}
                    index={index}
                    column={columnType}
                    isDragging={isDragging}
                    disabled={disabled}
                    onDragStart={(e) =>
                      handleDragStart(e, blockId, columnType, index)
                    }
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragEnter={() => handleDragEnter(columnType, index)}
                    onDrop={handleDrop}
                    onTouchStart={(e) =>
                      handleTouchStart(e, blockId, columnType, index)
                    }
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    itemClassName={itemClassName}
                    itemIconClassName={itemIconClassName}
                    itemLabelClassName={itemLabelClassName}
                  />
                );
              })}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <Box className={styles.container}>
      <div className={styles.columnsWrapper}>
        {renderColumn("left", displayLeftOrder, leftLabel)}
        {renderColumn("right", displayRightOrder, rightLabel)}
      </div>
    </Box>
  );
}
